import { useState, useEffect } from "react";
import { useActiveProfile } from "../../../contexts/ActiveBusinessContext";
import { useServices } from "../../../hooks/business/useServices";
import { PoundSterling, Loader2, Package, Info, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_SERVICES = [
  "Callout Fee",
  "Home Lockout", 
  "Car Lockout",
  "Lock Change",
  "Lock Repair",
  "Key Cutting",
  "Safe Opening",
  "Security Survey",
  "Emergency Service",
  "Lock Installation",
  "Master Key Systems",
  "UPVC Door Specialist"
];

export default function ServicesAndPricesTab() {
  const { profile } = useActiveProfile();
  const businessId = profile?.business_id || null;
  const {
    services,
    loading,
    saving,
    addService,
    removeService,
    toggleServiceOffered,
    updateServicePrice
  } = useServices(businessId);
  const [newService, setNewService] = useState({ name: "", price: 0 });
  const [showAddForm, setShowAddForm] = useState(false);
  const [loadingDefaults, setLoadingDefaults] = useState(false);

  // Load default services if none exist
  useEffect(() => {
    if (!loading && services.length === 0 && businessId) {
      loadDefaultServices();
    }
  }, [loading, services.length, businessId]);

  const loadDefaultServices = async () => {
    if (!businessId) return;
    
    setLoadingDefaults(true);
    try {
      for (const serviceName of DEFAULT_SERVICES) {
        await addService({
          service_name: serviceName,
          price: 0,
          is_offered: false
        });
      }
      toast.success('Default services loaded');
    } catch (error) {
      console.error('Failed to load default services:', error);
    } finally {
      setLoadingDefaults(false);
    }
  };

  const handleServiceToggle = async (serviceId: string) => {
    try {
      await toggleServiceOffered(serviceId);
    } catch (error) {
      console.error('Failed to toggle service:', error);
    }
  };

  const handlePriceChange = async (serviceId: string, price: string) => {
    try {
      const numericPrice = parseFloat(price) || 0;
      await updateServicePrice(serviceId, numericPrice);
    } catch (error) {
      console.error('Failed to update price:', error);
    }
  };

  const handleAddService = async () => {
    if (!newService.name.trim()) {
      toast.error("Service name is required");
      return;
    }

    try {
      await addService({
        service_name: newService.name,
        price: newService.price,
        is_offered: true
      });
      setNewService({ name: "", price: 0 });
      setShowAddForm(false);
    } catch (error) {
      console.error('Failed to add service:', error);
    }
  };

  const handleRemoveService = async (serviceId: string) => {
    try {
      await removeService(serviceId);
    } catch (error) {
      console.error('Failed to remove service:', error);
    }
  };

  if (loading || loadingDefaults) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          <p className="text-sm text-slate-500">{loadingDefaults ? 'Setting up default services...' : 'Loading your services...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-slate-900">Services & Prices</h2>
        <p className="mt-1 text-sm text-slate-500">
          Manage the locksmith services you offer and set your pricing.
        </p>
      </div>

      {/* Information Card */}
      <div className="mb-6 flex items-start gap-3 rounded-lg border border-blue-100 bg-blue-50 p-4">
        <div className="mt-0.5 rounded-full bg-blue-100 p-1">
          <Info className="h-5 w-5 text-blue-600" />
        </div>
        <div>
          <h3 className="font-medium text-blue-800">Service Visibility</h3>
          <p className="mt-1 text-sm text-blue-700">
            Toggle services on/off to control which ones are visible to customers.
            Set prices to £0 for services where you prefer to quote on a case-by-case basis.
          </p>
        </div>
      </div>

      {/* Services List */}
      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 flex justify-between items-center">
          <h3 className="flex items-center gap-2 text-sm font-medium text-slate-700">
            <Package className="h-4 w-4 text-slate-500" />
            Your Services
          </h3>
          <button
            type="button"
            onClick={() => setShowAddForm(!showAddForm)}
            className="inline-flex items-center rounded-md bg-slate-100 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <Plus className="mr-1 h-3.5 w-3.5" />
            Add Service
          </button>
        </div>
        
        <div className="divide-y divide-slate-100">
          {/* Add New Service Form */}
          {showAddForm && (
            <div className="p-4 bg-slate-50">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
                <div className="space-y-1">
                  <label htmlFor="new-service-name" className="block text-xs font-medium text-slate-700">
                    Service Name
                  </label>
                  <input
                    id="new-service-name"
                    type="text"
                    className="w-full rounded-md border border-slate-300 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    value={newService.name}
                    onChange={(e) => setNewService(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Emergency Callout"
                  />
                </div>
                
                <div className="space-y-1">
                  <label htmlFor="new-service-price" className="block text-xs font-medium text-slate-700">
                    Price (£)
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <PoundSterling className="h-4 w-4 text-slate-400" />
                    </div>
                    <input
                      id="new-service-price"
                      type="number"
                      min="0"
                      step="0.01"
                      className="w-full rounded-md border border-slate-300 pl-10 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      value={newService.price}
                      onChange={(e) => setNewService(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>
              
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="inline-flex items-center rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAddService}
                  className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-xs font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Add Service
                </button>
              </div>
            </div>
          )}
          
          {/* Services List */}
          {services.map((service) => (
            <div key={service.id} className="flex flex-col sm:flex-row sm:items-center gap-3 p-4">
              <div className="flex items-center gap-3 flex-1">
                <div className="flex h-5 items-center">
                  <input
                    id={`service-${service.id}`}
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    checked={service.is_offered}
                    onChange={() => handleServiceToggle(service.id)}
                    disabled={saving}
                  />
                </div>
                <label 
                  htmlFor={`service-${service.id}`} 
                  className={`flex-1 font-medium ${service.is_offered ? 'text-slate-800' : 'text-slate-400'}`}
                >
                  {service.service_name}
                </label>
              </div>
              
              <div className="flex items-center gap-2 ml-auto">
                <div className="relative w-32">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <PoundSterling className="h-4 w-4 text-slate-400" />
                  </div>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={service.price}
                    onChange={(e) => handlePriceChange(service.id, e.target.value)}
                    className={`w-full rounded-md border border-slate-300 pl-10 py-2 text-sm text-right shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 ${
                      !service.is_offered ? 'opacity-50 bg-slate-50' : 'bg-white'
                    }`}
                    placeholder="0.00"
                    disabled={!service.is_offered || saving}
                  />
                </div>
                
                <button
                  type="button"
                  onClick={() => handleRemoveService(service.id)}
                  className="p-1.5 text-slate-400 hover:text-red-500 focus:outline-none"
                  title="Remove service"
                  disabled={saving}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      {services.length > 0 && (
        <div className="mt-6 flex justify-end gap-2">
          <button 
            type="button" 
            className="inline-flex items-center rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            onClick={() => services.forEach(s => !s.is_offered && handleServiceToggle(s.id))}
            disabled={saving}
          >
            Enable All Services
          </button>
          <button 
            type="button" 
            className="inline-flex items-center rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            onClick={() => services.forEach(s => s.is_offered && handleServiceToggle(s.id))}
            disabled={saving}
          >
            Disable All Services
          </button>
        </div>
      )}
    </div>
  );
}